import ActionsDropdown from '@anm/components/ActionsDropdown';
import EditableField from '@anm/components/EditableField';
import Dialog from '@anm/components/modals/Dialog';
import UploadProjectsModal from '@anm/components/uploads/UploadProjectsModal';
import { DEFAULT_DROPDOWN_DELAY } from '@anm/constants/delays';
import isMobile from '@anm/helpers/is/isMobile';
import noop from '@anm/helpers/noop';
import getIsPastStream from '@anm/helpers/stream/getIsPastStream';
import sanitizeText from '@anm/helpers/string/sanitizeText';
import { getLinkTarget } from '@anm/helpers/upgradeLink';
import useSwitchState from '@anm/hooks/useSwitchState';
import { useCallback, FC } from 'react';

import DeleteModal from '../DeleteModal';
import InviteGuestsModal from '../InviteGuestsModal';

import ConfirmStopStream from './ConfirmStopStream';
import {
  ActionsWrapper,
  CardButton,
  CustomizeWebinar,
  DropdownActions,
  InfoWrapper,
  InnerTitleContent,
  InviteButton,
  Preview,
  PreviewWrapper,
  StreamDateWrapper,
  StreamItemBottomPanel,
  StreamItemTopPanel,
  StreamTextItem,
  StreamTitle,
  TitleAndDescription,
  Wrapper
} from './Wrapper';
import Badges from './components/Badges';
import StreamDestinations from './components/StreamDestinations';
import StreamStats from './components/StreamStats';
import {
  useDropdownActions,
  useDropdownOpenDirection,
  useShowDeleteModal,
  useStreamBadge,
  useStreamErrors
} from './hooks';
import { DeleteParams, StreamItemProps } from './types';

export * from './types';

const StreamItem: FC<StreamItemProps> = props => {
  const { startedAt, schedule, maxGuests, duration, webinarId, onCustomize = noop } = props;
  const { createDate, studioLink, description, destinations, streamError, isFirstCard } = props;
  const { pausedDuration, uploadProjects = [], canDebug = false, isMaxSubscription } = props;
  const { isDeletePending, onStopStream, onTitleUpdate, fetchUploadProjects } = props;
  const { additionalActions = [], onOpenEditModal, onDeleteRecording, onOpenRecordingFolder, ...actionProps } = props;
  const { src, date, kind, title, state, status, hasFile, streamId, meta } = props;

  const lastStartedAt = meta.paused?.lastStartedAt;
  const [isUploadProjects, openProjects, closeProjects] = useSwitchState();

  const [isOpenDeleteModal, handelOpenDeleteModal, handelCloseDeleteModal, isDeleteFromWave] = useShowDeleteModal(
    hasFile,
    fetchUploadProjects,
    uploadProjects
  );
  const isPastStream = getIsPastStream(state);
  const isUpcomingRecording = state === 'SCHEDULED' && kind === 'RECORD';

  const [isInviteModalOpen, openInviteModal, closeInviteModal] = useSwitchState();
  const [isStopStreamModal, openStopStreamModal, closeStopStreamModal] = useSwitchState();
  const badgeSchedule = isPastStream ? null : schedule;
  const streamBadge = useStreamBadge({ schedule: badgeSchedule, kind, status });

  const mainActions = useDropdownActions({
    canDebug,
    webinarId,
    hasDestinations: !!destinations.length,
    onOpenEditModal,
    openStopStreamModal,
    onOpenRecordingFolder,
    openDeleteModal: handelOpenDeleteModal,
    ...actionProps
  });
  const actions = [...additionalActions, ...mainActions];
  const [actionsRef, dropdownDirection] = useDropdownOpenDirection(actions.length, isFirstCard);

  const handleDelete = useCallback(
    (props: Omit<DeleteParams, 'streamId'>) => {
      isDeleteFromWave ? onDeleteRecording?.() : actionProps.onDelete?.({ streamId, ...props });
      isDeleteFromWave && handelCloseDeleteModal();
    },
    [streamId, actionProps.onDelete, onDeleteRecording, isDeleteFromWave]
  );

  const deleteModalTitle = isDeleteFromWave || kind === 'RECORD' ? `Delete recording` : 'Delete stream';

  const dateSlug = date ? ` (${date})` : '';
  const titleAndDate = `<b>${title}</b>${dateSlug}`;
  const destinationSlug = destinations.length ? ' from all selected destinations' : '';
  const modalDescription = isDeleteFromWave
    ? 'This action will delete all files related to this stream from Wave.video platform, but will keep it on the destinations.'
    : `Your ${titleAndDate} will be deleted${destinationSlug}.`;

  const projectModalDescription = `${titleAndDate} is used in these projects.`;
  const canDeleteFile = (hasFile && uploadProjects?.length === 0) || isUpcomingRecording;

  const type = kind === 'RECORD' ? 'recording' : 'stream';
  const isMobileDevice = isMobile();
  const canEditDestinations = !isMobileDevice && kind === 'LIVE' && state === 'SCHEDULED';

  const [isStreamError, destinationsWithError] = useStreamErrors(streamError);
  const errorDestination = destinations.find(d => destinationsWithError?.[d.id]);

  const dividerIndexes = isPastStream
    ? [actions.findIndex(a => a.name === 'Download video'), actions.findIndex(a => a.name === 'Duplicate') - 1]
    : [];

  return (
    <>
      {isUploadProjects && (
        <UploadProjectsModal
          title={deleteModalTitle}
          description={projectModalDescription}
          projects={uploadProjects}
          onButtonClick={closeProjects}
        />
      )}
      {isStopStreamModal && (
        <Dialog onModalClose={closeStopStreamModal} onSubmit={onStopStream}>
          <ConfirmStopStream
            stopText={`Stop ${type}`}
            description={`Are you sure to stop ${type} <b>${title}</b>?`}
            onClose={closeStopStreamModal}
            onStopStream={onStopStream}
          />
        </Dialog>
      )}
      {isOpenDeleteModal && actionProps.onDelete && destinations && (
        <DeleteModal
          list={destinations}
          hasUpload={!!meta.uploadId}
          isLive={kind === 'LIVE'}
          isPastStream={isPastStream}
          openProjects={openProjects}
          title={deleteModalTitle}
          showFolderCheckbox={hasFile || kind === 'HOSTED'}
          isPending={!!isDeletePending}
          description={modalDescription}
          canDeleteFile={canDeleteFile}
          isDeleteFromWave={isDeleteFromWave}
          onModalClose={handelCloseDeleteModal}
          onDelete={handleDelete}
        />
      )}
      {isInviteModalOpen && (
        <InviteGuestsModal {...{ maxGuests, isMaxSubscription }} link={studioLink} onClose={closeInviteModal} />
      )}
      <Wrapper hasPreview={!!src}>
        <StreamItemTopPanel hasPreview={!!src}>
          {src && (
            <PreviewWrapper>
              <Preview src={src} alt={title} />
            </PreviewWrapper>
          )}
          <TitleAndDescription>
            <InnerTitleContent>
              <EditableField
                maxLength={100}
                hoverVariant="pencil"
                initialValue={sanitizeText(title)}
                onChange={onTitleUpdate}
                isFocusedOnClick={state === 'SCHEDULED' || kind === 'RECORD'}
              />
              <StreamTitle level={2} description={sanitizeText(description)} />
            </InnerTitleContent>
          </TitleAndDescription>
          <StreamDateWrapper>
            {date && <StreamTextItem variant="big-black" dangerouslySetInnerHTML={{ __html: date }} />}
            <StreamTextItem variant="small-grey">Created {createDate}</StreamTextItem>
          </StreamDateWrapper>
        </StreamItemTopPanel>
        <StreamItemBottomPanel>
          <InfoWrapper>
            <Badges {...{ streamBadge, kind, state, schedule, errorDestination, isStreamError, onOpenEditModal }} />
            <StreamStats
              webinarStats={meta?.webinarStats}
              lastStartedTime={lastStartedAt ? new Date(lastStartedAt).getTime() : null}
              {...{ kind, state, startedAt, duration, destinations, pausedDuration }}
            />
          </InfoWrapper>
          {!!webinarId && (
            <CustomizeWebinar variant="link" size="small" onClick={onCustomize}>
              Customize
            </CustomizeWebinar>
          )}
          <ActionsWrapper>
            <StreamDestinations {...{ destinations, state, webinarId }} />
            {canEditDestinations && (
              <CardButton variant="silver-border" size="medium" onClick={actionProps.onEditDestinations}>
                Edit destinations
              </CardButton>
            )}
            <CardButton variant="silver-border" size="medium" href={studioLink} target={getLinkTarget()}>
              Open studio
            </CardButton>
            {!isPastStream && (
              <InviteButton
                size="medium"
                variant="silver-border"
                icon="images/streaming/person_invite_black.svg"
                onClick={openInviteModal}
              />
            )}
            <DropdownActions ref={actionsRef}>
              <ActionsDropdown
                autoClose
                actions={actions}
                openDelay={DEFAULT_DROPDOWN_DELAY}
                closeDelay={DEFAULT_DROPDOWN_DELAY}
                direction={dropdownDirection}
                dividerIndexes={dividerIndexes}
              />
            </DropdownActions>
          </ActionsWrapper>
        </StreamItemBottomPanel>
      </Wrapper>
    </>
  );
};

export default StreamItem;
